import { useEffect, useRef, useState } from 'react';
import {
  Grid,
  Card,
  Paper,
  CardActions,
  Button,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Contact,
  ExpertSystemOptions,
  getExpertSystemOptions,
  updateExpertSystemOptions,
} from './simulator.slice';
import { useDispatch, useSelector } from 'react-redux';
import { BroadcastChannel } from 'broadcast-channel';
import { getCopilotConfig } from '@nice-ccf/ui-components';
import classes from './simulator.module.scss';
import axios from 'axios';
import { DigitalChannelType } from '@nice-ccf/core-sdk';
import { generateRandomId, processResultToArray } from './simulator-util';
import {
  API_BASE_URL,
  callApi,
  EXPERT_VERTICALS,
} from 'libs/react/ui-components/src/lib/ccf-app-space/ccf-copilot/ccf-copilot-api';
import {
  CcfSimulatorUploadIcon,
  CcfTypography,
  CcfTextField,
} from '@nice-ccf/ui-controls';
import { useMsal } from '@azure/msal-react';

/**
 * To show the contact detail for the selected contact from the contact list card. This will also let user to edit the values for the contact detail
 * @param props - it will take selected contact detail, and onchange hadnler
 * @returns
 * @example
 */
export default function CopilotDemoSettingsConfig(props: {
  selectedContact: Contact;
  handleButtonClick: () => void;
  buttonText: string;
  isLiveCopilotEnabled: boolean;
  setToastMessage: any;
  setShowToast: any;
  handleInputChange: any;
}) {
  const { accounts } = useMsal();
  const loggedInUser = accounts[0]?.username?.toLowerCase();
  const copilotConfiguration: any = useSelector(getCopilotConfig);

  const [localConfiguration, setLocalConfiguration] =
    useState(copilotConfiguration);
  const {
    selectedContact,
    isLiveCopilotEnabled,
    setToastMessage,
    setShowToast,
    handleInputChange,
  } = props;
  const dispatch = useDispatch();
  const expertSystemOptionsFromSelector = useSelector(getExpertSystemOptions);
  const [expertSystemOptions, setExpertSystemOptions] = useState(
    expertSystemOptionsFromSelector
  );

  const demoType = localConfiguration?.demoType;

  useEffect(() => {
    callApi(API_BASE_URL + EXPERT_VERTICALS, 'GET')
      .then((response: any) => {
        const expertSystemOptionsFromAPI: ExpertSystemOptions = response.reduce(
          (expertSystemOptions: ExpertSystemOptions, item: any) => {
            expertSystemOptions[item.expertPathName] = {
              ...item,
              associatedFiles: [...item.associatedFiles, 'Custom'],
              copilotPersona: item.persona,
            };
            return expertSystemOptions;
          },
          {} as ExpertSystemOptions
        );
        setExpertSystemOptions(expertSystemOptionsFromAPI);
        dispatch(updateExpertSystemOptions(expertSystemOptionsFromAPI as any));
      })
      .catch((error) => {
        console.error(
          'Error fetching expert verticals from API:',
          error.message
        );
      });
  }, [updateExpertSystemOptions]);

  const [filteredExpertSystemOptions, setFilteredExpertSystemOptions] =
    useState<ExpertSystemOptions>({});

  const DEMO_TYPES = {
    VERTICAL: 'Vertical',
    CUSTOMER: 'Customer',
  };

  /**
   *
   * @param expertSystemOptions
   * @param filterForVertical
   * @returns filtered expert system options
   * @example filterExpertSystemOptions(expertSystemOptions, true)
   */
  const filterExpertSystemOptions = (
    expertSystemOptions: ExpertSystemOptions,
    filterForVertical: boolean
  ) => {
    const result: Partial<ExpertSystemOptions> = {};
    for (const key in expertSystemOptions) {
      if (expertSystemOptions.hasOwnProperty(key)) {
        const formatUsername = (email: string) => {
          const [firstName, lastName] = email.split('@')[0].split('.');
          return `${firstName.charAt(0).toUpperCase() + firstName.slice(1)} ${lastName.charAt(0).toUpperCase() + lastName.slice(1)}`;
        };
        const value = expertSystemOptions[key];
        const isOwner = value?.owner === formatUsername(loggedInUser);
        const isActiveAndAccessible = value?.status === 'Active' && (value?.visibility === 'Public' || isOwner);

        if (
          (filterForVertical
            ? (value?.demoType === 'Vertical' && isActiveAndAccessible)
            : (value?.demoType === 'Custom' && isActiveAndAccessible)
        )) {
          result[key] = value;
        }
      }
    }
    return result as ExpertSystemOptions;
  };

  useEffect(() => {
    setFilteredExpertSystemOptions(
      filterExpertSystemOptions(
        expertSystemOptions as ExpertSystemOptions,
        demoType === DEMO_TYPES.VERTICAL
      )
    );
  }, [demoType]);

  const handleSaveClick = async ({
    index,
    keyName,
    value,
  }: {
    index: string;
    keyName: string;
    value: string;
  }) => {
    const channel: BroadcastChannel<any> = new BroadcastChannel(
      'simulator_channel'
    );
    await channel.postMessage({
      type: 'saveCopilotConfig',
      data: localConfiguration,
    });
    await channel.close();
  };

  const handleSaveIndexClick = async ({
    index,
    keyName,
    value,
  }: {
    index: string;
    keyName: string;
    value: string;
  }) => {
    const {
      expertCopilotURL,
      expertCopilotClientKey,
      expertCopilotClientSecret,
      kBAnswersPath,
      salesOffersPath,
      compliancePath,
      expertPathName,
      copilotPersona,
    } = expertSystemValues?.[index] || {};

    const channel: BroadcastChannel<any> = new BroadcastChannel(
      'simulator_channel'
    );
    await channel.postMessage({
      type: 'saveCopilotConfig',
      data: {
        ...localConfiguration,
        expertCopilotURL,
        expertCopilotClientKey: expertCopilotClientKey?.substring(0, 9),
        expertCopilotClientSecret: expertCopilotClientSecret?.substring(0, 9),
        kBAnswersPath,
        salesOffersPath,
        compliancePath,
        path: expertPathName,
        botPersona: copilotPersona ?? '',
        [keyName]: value,
      },
    });
    await channel.close();
  };

  const handleIndexInputChange = async (e: any, key: string) => {
    const value = e?.target?.value;
    let updatedConfig = { ...localConfiguration, [key]: value };

    switch (key) {
      case 'index':
        handleSaveIndexClick({
          index: value ?? '',
          keyName: key,
          value: value,
        });
        updatedConfig = {
          ...updatedConfig,
          botPersona: expertSystemOptions?.[value]?.copilotPersona,
        };
        break;
      case 'language':
        updatedConfig = {
          ...updatedConfig,
          path: 'custom',
          associatedFile: '',
          botPersona: '',
          demoType: '',
        };
        break;
      case 'demoType':
        updatedConfig = {
          ...updatedConfig,
          path: 'custom',
          associatedFile: '',
          botPersona: '',
        };
        break;
      default:
        break;
    }

    setLocalConfiguration(updatedConfig);
  };

  const expertSystemValues: ExpertSystemOptions =
    expertSystemOptions as ExpertSystemOptions;

  useEffect(() => {
    setLocalConfiguration(copilotConfiguration);
  }, [copilotConfiguration]);

  const associatedFiles =
    expertSystemValues?.[localConfiguration.path]?.associatedFiles;
  const configFileUpload = useRef<any>(null);

  /**
   * Function to get file info of attchment being uploaded
   * @example getConfigFileInfo()
   */
  const getConfigFileInfo = () => {
    // setProgress(0);
    const data = new FormData();
    data.append('file', configFileUpload.current.files);
    const attachmentCopy = [];
    for (const item of configFileUpload.current.files) {
      attachmentCopy.push({ name: item.name, progress: 0 });
    }
    getConfigFileContent();
    configFileUpload.current.value = '';
  };

  /**
   * Function to get file content of JSON file being uploaded
   * @example getConfigFileContent()
   */
  const getConfigFileContent = () => {
    const file = configFileUpload.current.files[0];
    const fr = new FileReader();
    fr.onload = receivedConfigText;
    fr.readAsText(file);
  };

  /**
   * Function to receive text file content of json file being uploaded
   * @example receivedConfigText()
   */
  const receivedConfigText = (e: any) => {
    const lines = e.target.result;
    const newConfig = JSON.parse(lines);
    const updatedConfigs: ExpertSystemOptions = {} as ExpertSystemOptions;
    newConfig.customConfig.forEach((config: any) => {
      const key = config.expertPathName ?? 'custom';
      updatedConfigs[key] = {
        expertSystem: config.expertSystem ?? 'Custom',
        expertPathName: config.expertPathName ?? 'custom',
        associatedFiles: [],
        expertCopilotURL: config.expertCopilotUrl ?? '',
        kBAnswersPath: config.kbAnswersPath ?? 'KnowledgeContent',
        salesOffersPath: config.salesOffersPath ?? 'SalesContent',
        compliancePath: config.compliancePath ?? 'ComplianceContent',
        expertCopilotClientKey: config.expertCopilotClientKey ?? '',
        expertCopilotClientSecret: config.expertCopilotClientSecret ?? '',
        copilotPersona: config.copilotPersona ?? '',
        status: config.status ?? '',
        demoType: config.demoType ?? '',
        visibility: config.visibility ?? '',
        owner: config.owner ?? '',
        description: config.description ?? '',
        lastUpdatedBy: config.lastUpdatedBy ?? '',
        lastUpdatedTime: config.lastUpdatedTime ?? '',
      };
    });
    const mergedConfigs = { ...expertSystemOptions, ...updatedConfigs };
    setExpertSystemOptions(mergedConfigs);
    dispatch(updateExpertSystemOptions(mergedConfigs as any));
  };

  const readAssociatedFileContent = (e: any) => {
    const fileName = e.target.value;
    axios({
      url: './../assets/' + fileName,
      method: 'GET',
      responseType: 'blob',
    })
      .then((response) => {
        const fr = new FileReader();
        fr.onload = receivedAssociatedFileText;
        fr.readAsText(response.data); // Pass the blob data to readAsText
      })
      .catch((error) => {
        console.error('Error fetching file:', error);
      });
    handleInputChange('customerChatReply', '');
  };

  const receivedAssociatedFileText = (e: any) => {
    if (isLiveCopilotEnabled) {
      const {
        dataFile: dataFileArr,
        audioFileName,
        customerFullName: customerName,
        agentName,
        interactions: previousInteractions,
        intents,
        customReplies,
        autopilotSummary,
        transferSummary,
        customerSentiment,
        confidence,
        emailId,
        emailSubject,
        receiverEmail,
        attachments,
      } = JSON.parse(e.target.result);
      const [firstName, lastName] = customerName.split(' ');
      const id = generateRandomId(agentName, customerName);
      const processedArray = processResultToArray(
        dataFileArr,
        selectedContact.media,
        agentName
      );
      const inputChanges = {
        customerChatReplyJsonData: processedArray,
        perviousInteractionJsonData: previousInteractions,
        intents,
        customReplies,
        customerSentiment,
        confidence,
        audioFileName,
        firstName,
        lastName,
        autopilotSummary,
        transferSummary,
        emailId,
        emailSubject,
        receiverEmail,
        attachments,
        channel:
          selectedContact?.channel === 'Email'
            ? DigitalChannelType.EMAIL
            : DigitalChannelType.CHAT,
        id,
      };
      if (selectedContact.media === 'Voice') {
        handleInputChange('messageType', '253-895-8956');
        handleInputChange('queueName', 'Cust. Service Request');
      }
      Object.entries(inputChanges).forEach(([key, value]) =>
        handleInputChange(key, value)
      );
      setToastMessage('File Saved');
      setShowToast(true);
    }
  };
  const supportedLanguages = [
    { code: 'en-US', label: 'English' },
    { code: 'pt-BR', label: 'Português' },
  ];

  return (
    <Grid className={classes.simulatorColumn}>
      <Paper elevation={3} className={classes.simulatorPaperWithFullheight}>
        <Card className={classes.heightInherit}>
          <CcfTypography
            className={classes.txtChatMsg}
            style={{ marginTop: '20px', marginLeft: '15px' }}
          >
            COPILOT DEMO SETTINGS
          </CcfTypography>
          <CardContent>
            <form
              className={classes.root + ' ' + classes.simulatorForm}
              noValidate
              autoComplete="off"
            >
              <FormControl
                fullWidth
                variant="outlined"
                className={classes.formControl}
                style={{
                  width: '100%',
                  marginBottom: '15px',
                }}
              >
                <InputLabel>Output Language</InputLabel>
                <Select
                  labelId=""
                  id=""
                  value={localConfiguration.language}
                  label="Output Language"
                  onChange={(e: any) => {
                    readAssociatedFileContent(e);
                    handleIndexInputChange(e, 'language');
                  }}
                >
                  {supportedLanguages?.map((lang, index) => {
                    return (
                      <MenuItem key={index} value={lang.code}>
                        {lang.label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <FormControl
                fullWidth
                variant="outlined"
                className={classes.formControl}
                style={{
                  width: '100%',
                  marginBottom: '15px',
                }}
              >
                <InputLabel>Demo Type</InputLabel>
                <Select
                  labelId=""
                  id=""
                  value={localConfiguration.demoType}
                  label="Demo Type"
                  onChange={(e: any) => {
                    handleIndexInputChange(e, 'demoType');
                  }}
                >
                  <MenuItem value="Vertical">{DEMO_TYPES.VERTICAL}</MenuItem>
                  <MenuItem value="Customer">{DEMO_TYPES.CUSTOMER}</MenuItem>
                </Select>
              </FormControl>
              {demoType && (
                <FormControl
                  fullWidth
                  variant="outlined"
                  className={classes.formControl}
                  style={{
                    width: '100%',
                    marginBottom: '15px',
                  }}
                >
                  <InputLabel>Demo</InputLabel>
                  <Select
                    labelId=""
                    id=""
                    value={localConfiguration.path}
                    label="Demo"
                    onChange={(e: any) => {
                      handleIndexInputChange(e, 'index');
                      handleIndexInputChange(e, 'path');
                    }}
                  >
                    {Object.keys(
                      filteredExpertSystemOptions as ExpertSystemOptions
                    ).map((key) => (
                      <MenuItem key={key} value={key}>
                        {filteredExpertSystemOptions &&
                          filteredExpertSystemOptions[
                            key as keyof typeof filteredExpertSystemOptions
                          ].expertSystem}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {localConfiguration.path !== 'custom' && (
                <FormControl
                  fullWidth
                  variant="outlined"
                  className={classes.formControl}
                  style={{
                    width: '100%',
                    marginBottom: '15px',
                  }}
                >
                  <InputLabel>Demo JSON</InputLabel>
                  <Select
                    labelId=""
                    id=""
                    value={localConfiguration.associatedFile}
                    label="Demo JSON"
                    onChange={(e: any) => {
                      readAssociatedFileContent(e);
                      handleIndexInputChange(e, 'associatedFile');
                    }}
                  >
                    {associatedFiles?.map((file: any, index: any) => {
                      return (
                        <MenuItem key={index} disabled={file.status === 'Inactive'} value={`${file.name}.json`}>
                          {file.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              )}
              {localConfiguration.path === 'custom' && (
                <div
                  className={classes.uploadFileInnerBox}
                  style={{ marginBottom: '10%' }}
                >
                  <input
                    type="file"
                    hidden
                    ref={configFileUpload}
                    multiple
                    onChange={(e) => {
                      getConfigFileInfo();
                    }}
                  />
                  <CcfSimulatorUploadIcon
                    onClick={() => {
                      configFileUpload.current.click();
                    }}
                  />
                  <CcfTypography className={classes.txtUploadTemplate}>
                    Load custom config
                  </CcfTypography>
                </div>
              )}
              <CcfTextField
                id="outlined-basic"
                className={classes.input}
                label="Copilot Persona"
                multiline
                rows={8}
                maxRows={21}
                placeholder="add copilot persona"
                variant="outlined"
                value={localConfiguration.botPersona}
                InputLabelProps={{ shrink: true }}
                onChange={(e: any) => {
                  handleIndexInputChange(e, 'botPersona');
                }}
              />
              {localConfiguration.path === 'custom' && (
                <>
                  <CcfTextField
                    id="outlined-basic"
                    className={classes.input}
                    label="Expert Key"
                    variant="outlined"
                    type="password"
                    defaultValue={
                      expertSystemValues?.[localConfiguration.path]
                        ?.expertCopilotClientKey
                    }
                    value={localConfiguration.expertCopilotClientKey}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e: any) => {
                      handleIndexInputChange(e, 'expertCopilotClientKey');
                    }}
                  />
                  <CcfTextField
                    id="outlined-basic"
                    className={classes.input}
                    label="Expert Secret"
                    variant="outlined"
                    type="password"
                    defaultValue={
                      expertSystemValues?.[localConfiguration.path]
                        ?.expertCopilotClientSecret
                    }
                    value={localConfiguration.expertCopilotClientSecret}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e: any) => {
                      handleIndexInputChange(e, 'expertCopilotClientSecret');
                    }}
                  />
                  <CcfTextField
                    id="outlined-basic"
                    className={classes.input}
                    label="Site"
                    variant="outlined"
                    defaultValue={
                      expertSystemValues?.[localConfiguration.path]
                        ?.expertCopilotURL
                    }
                    value={localConfiguration.expertCopilotURL}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e: any) => {
                      handleIndexInputChange(e, 'expertCopilotURL');
                    }}
                  />

                  <CcfTextField
                    id="outlined-basic"
                    className={classes.input}
                    label="Knowledge Path"
                    variant="outlined"
                    defaultValue={
                      expertSystemValues?.[localConfiguration.path]
                        ?.kBAnswersPath
                    }
                    value={localConfiguration.kBAnswersPath}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e: any) => {
                      handleIndexInputChange(e, 'kBAnswersPath');
                    }}
                  />
                  <CcfTextField
                    id="outlined-basic"
                    className={classes.input}
                    label="Offers Path"
                    variant="outlined"
                    defaultValue={
                      expertSystemValues?.[localConfiguration.path]
                        ?.salesOffersPath
                    }
                    value={localConfiguration.salesOffersPath}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e: any) => {
                      handleIndexInputChange(e, 'salesOffersPath');
                    }}
                  />
                  <CcfTextField
                    id="outlined-basic"
                    className={classes.input}
                    label="Compliance Path"
                    variant="outlined"
                    defaultValue={
                      expertSystemValues?.[localConfiguration.path]
                        ?.compliancePath
                    }
                    value={localConfiguration.compliancePath}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e: any) => {
                      handleIndexInputChange(e, 'compliancePath');
                    }}
                  />
                </>
              )}
            </form>
          </CardContent>
          <CardActions className={classes.simulatorSaveButton}>
            <Button
              variant="contained"
              color="primary"
              onClick={(e) => handleSaveClick(copilotConfiguration.path ?? '')}
              style={{ padding: '8px' }}
            >
              Apply
            </Button>
          </CardActions>
        </Card>
      </Paper>
    </Grid>
  );
}
